// src/components/profile/PushNotificationTester.tsx
// Phase 5: 完整的推播通知測試工具

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import {
  sendTestPushNotification,
  sendCustomPushNotification,
} from '../../services/api/pushNotifications';

interface TestResult {
  type: 'success' | 'error' | 'info';
  message: string;
  details?: Record<string, unknown>;
}

/**
 * 推播通知測試工具
 *
 * 功能：
 * 1. 基礎測試推播
 * 2. 自訂推播內容測試
 * 3. 顯示測試結果和詳細資訊
 */
export const PushNotificationTester: React.FC = () => {
  const { currentUser } = useAuth();
  const {
    permission,
    isSupported,
    isLoading,
    requestPermission,
    fcmToken,
  } = useNotificationPermission();

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 自訂推播表單
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [customClickAction, setCustomClickAction] = useState('/today');

  // 快速測試推播
  const handleQuickTest = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await sendTestPushNotification();

      if (result.success) {
        setTestResult({
          type: 'success',
          message: '✅ Test notification sent successfully!',
          details: result.results,
        });
      } else {
        setTestResult({
          type: 'error',
          message: `❌ Failed to send: ${result.error}`,
        });
      }
    } catch (error: unknown) {
      setTestResult({
        type: 'error',
        message: `❌ Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    } finally {
      setIsTesting(false);
    }
  };

  // 自訂推播測試
  const handleCustomTest = async () => {
    if (!customTitle || !customBody) {
      setTestResult({
        type: 'error',
        message: '❌ Please fill in both title and body',
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await sendCustomPushNotification(
        {
          title: customTitle,
          body: customBody,
        },
        {
          clickAction: customClickAction,
          type: 'custom_test',
        },
        'normal'
      );

      if (result.success) {
        setTestResult({
          type: 'success',
          message: '✅ Custom notification sent successfully!',
          details: result.results,
        });
        // 清空表單
        setCustomTitle('');
        setCustomBody('');
        setCustomClickAction('/today');
      } else {
        setTestResult({
          type: 'error',
          message: `❌ Failed to send: ${result.error}`,
        });
      }
    } catch (error: unknown) {
      setTestResult({
        type: 'error',
        message: `❌ Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start">
          <span className="text-3xl mr-3">⚠️</span>
          <div>
            <h3 className="font-semibold text-yellow-900 text-lg mb-2">
              Browser Not Supported
            </h3>
            <p className="text-yellow-800">
              Your browser doesn't support push notifications. Please use Chrome, Firefox, or Edge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-6 bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg border border-sky-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              🧪 Push Notification Tester
            </h2>
            <p className="text-gray-700">
              Test push notification functionality and view detailed results
            </p>
          </div>

          {/* 權限狀態指示器 */}
          <div className="ml-4">
            {permission === 'granted' && (
              <div className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium text-sm">Permission Granted</span>
              </div>
            )}
            {permission === 'denied' && (
              <div className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="font-medium text-sm">Permission Denied</span>
              </div>
            )}
            {permission === 'default' && (
              <button
                onClick={requestPermission}
                className="px-4 py-2 btn-primary rounded-lg font-medium text-sm"
              >
                Request Permission
              </button>
            )}
          </div>
        </div>
      </div>

      {permission === 'granted' && fcmToken && (
        <>
          {/* 快速測試 */}
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Test
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Send a basic test notification to verify the system is working
            </p>

            <button
              onClick={handleQuickTest}
              disabled={isTesting}
              className="w-full px-4 py-3 btn-accent rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTesting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                '🚀 Send Test Notification'
              )}
            </button>
          </div>

          {/* 進階測試 */}
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Custom Test
              </h3>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                {showAdvanced ? 'Hide' : 'Show'}
              </button>
            </div>

            {showAdvanced && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Customize the notification content and behavior
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="e.g., Task Reminder"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Body *
                  </label>
                  <textarea
                    value={customBody}
                    onChange={(e) => setCustomBody(e.target.value)}
                    placeholder="e.g., You have 3 tasks due today"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Click Action (URL)
                  </label>
                  <input
                    type="text"
                    value={customClickAction}
                    onChange={(e) => setCustomClickAction(e.target.value)}
                    placeholder="/today"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The page to open when the notification is clicked
                  </p>
                </div>

                <button
                  onClick={handleCustomTest}
                  disabled={isTesting || !customTitle || !customBody}
                  className="w-full px-4 py-3 btn-secondary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTesting ? 'Sending...' : '📤 Send Custom Notification'}
                </button>
              </div>
            )}
          </div>

          {/* 測試結果 */}
          {testResult && (
            <div
              className={`p-6 rounded-lg border ${
                testResult.type === 'success'
                  ? 'bg-green-50 border-green-200'
                  : testResult.type === 'error'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-sky-50 border-sky-200'
              }`}
            >
              <div className="flex items-start">
                <span className="text-2xl mr-3">
                  {testResult.type === 'success' ? '✅' : testResult.type === 'error' ? '❌' : 'ℹ️'}
                </span>
                <div className="flex-1">
                  <p
                    className={`font-medium mb-2 ${
                      testResult.type === 'success'
                        ? 'text-green-900'
                        : testResult.type === 'error'
                        ? 'text-red-900'
                        : 'text-sky-900'
                    }`}
                  >
                    {testResult.message}
                  </p>

                  {testResult.details && (
                    <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Result Details:</p>
                      <pre className="text-xs text-gray-600 overflow-x-auto">
                        {JSON.stringify(testResult.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 設備資訊 */}
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Device Information
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">User ID:</span>
                <span className="text-gray-900 font-mono text-xs">
                  {currentUser?.uid.substring(0, 20)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Permission:</span>
                <span className="text-gray-900 font-medium">{permission}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">FCM Token:</span>
                <span className="text-gray-900 font-mono text-xs truncate max-w-xs">
                  {fcmToken.substring(0, 30)}...
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {permission === 'denied' && (
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start">
            <span className="text-3xl mr-3">🚫</span>
            <div>
              <h3 className="font-semibold text-red-900 text-lg mb-2">
                Permission Denied
              </h3>
              <p className="text-red-800 mb-3">
                Push notifications are blocked. To enable them:
              </p>
              <ol className="list-decimal list-inside text-red-800 space-y-1 text-sm">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Find "Notifications" and change to "Allow"</li>
                <li>Reload this page</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
